import {onMounted, onUnmounted, ref} from 'vue'
import {get, post} from '@/services/infrastructure/api.service'
import {LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {ResourceLink, ResourceLinkSchema} from '@/resource-link/resourceLink'
import {z} from 'zod'

const COMMENTS_PATH = '/comments'

const INITIATE_THREAD = COMMENTS_PATH + '/:initiate'
const SET_THREAD_RESOLVED_PATH = COMMENTS_PATH + '/:set-resolved'
const ADD_COMMENT_PATH = COMMENTS_PATH + '/:add-comment'
//const UPDATE_COMMENT_PATH = COMMENTS_PATH + '/:update-comment'

const CommentSchema = z.object({
    uuid: z.uuid(),
    body: z.string(),
    photoUrl: z.string(),
    createdBy: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
})

const CreateCommentSchema = z.object({
    body: z.string().nonempty(),
    resourceLink: ResourceLinkSchema,
    thread: z.string().nullable(),
})

const ResolveThreadSchema = z.object({
    resolved: z.boolean().nonoptional(),
    thread: z.string().nonempty(),
})

const CommentThreadSchema = z.object({
    uuid: z.uuid(),
    resolved: z.boolean(),
    resourceLink: ResourceLinkSchema, //ResourceLinkCodec,
    comments: z.array(CommentSchema)
})

export type ResourceLinkComment = z.infer<typeof CommentSchema>
export type CommentCreate = z.infer<typeof CreateCommentSchema>
export type ResolveThread = z.infer<typeof ResolveThreadSchema>
export type CommentThread = z.infer<typeof CommentThreadSchema>

const unresolvedCommentThreads = ref<CommentThread[]>([])

const saveComment = async (comment: CommentCreate): Promise<CommentThread[]> => {
    if (comment.thread) {
        return post(ADD_COMMENT_PATH, {uuid: comment.thread, body: comment.body})
    } else {
        return post(INITIATE_THREAD, {resourceLink: comment.resourceLink, body: comment.body})
    }
}

const resolveThread = async (thread: ResolveThread): Promise<CommentThread[]> => {
    const threads: Promise<CommentThread[]> = await post(SET_THREAD_RESOLVED_PATH, {uuid: thread.thread, resolved: thread.resolved})
    await fetchUnresolvedThreads()
    return threads
}

const fetchThread = async (uuid: string): Promise<CommentThread |undefined> => {
    return await get(COMMENTS_PATH + '/' + uuid)
}

const fetchThreadsByUrn = async (urnIdentifier: string): Promise<CommentThread[]> => {
    return await get<CommentThread[]>(COMMENTS_PATH + '/urn', {urnIdentifier}) ?? []
}

const fetchUnresolvedThreads = async () => {
    unresolvedCommentThreads.value = await get<CommentThread[]>(COMMENTS_PATH + '/resolved', {
        resolved: false
    }) ?? []
}

const clear = () => {
    unresolvedCommentThreads.value = []
}

const activeResourceLinkElement = ref<HTMLSpanElement | null>(null)
const commentPopupVisible = ref(false)
const commentThreadUrn = ref<ResourceLink>()

export const useComment = () => {
    const {onEvents, cleanUp} = useSingletonEmitter('comment')

    const closePopup = () => {
        commentPopupVisible.value = false
        commentThreadUrn.value = undefined
        activeResourceLinkElement.value = null
    }

    onMounted(() => {
        onEvents([ORG_SELECTED, ORG_SWITCHED], async () => {
            await fetchUnresolvedThreads()
        })

        onEvents([NO_ORG_SELECTED, LOGOUT], clear)
    })

    onUnmounted(() => {
        cleanUp()
    })

    return {
        activeResourceLinkElement,
        commentPopupVisible,
        commentThreadUrn,
        unresolvedCommentThreads,
        closePopup,
        saveComment,
        resolveThread,
        fetchThread,
        fetchThreadsByUrn
    }
}